import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useIntl, FormattedMessage } from 'react-intl';
import { Icon } from '../../design-system/icons/Icon';
import { APPLICATION_STATES } from './constants';
import type { ApplicationState } from '../../types/tracked-job';
import { Msg, useFormatMessage } from '../../i18n-review/client/Msg';
export interface ApplicationStateMenuProps {
  currentApplicationState: ApplicationState | null | undefined;
  isApplied: boolean;
  isHidden: boolean;
  onSetApplicationState: (state: ApplicationState) => void;
  onToggleApplied: () => void;
  onToggleHide: () => void;
  onClose: () => void;
  className?: string;
  triggerRef?: React.RefObject<HTMLElement>;
  usePortal?: boolean;
}

/**
 * Dropdown menu for managing application states and flags
 * Includes mutually exclusive states (interviewing, offer, rejected, hired)
 * and independent flags (applied, hidden)
 */
export const ApplicationStateMenu: React.FC<ApplicationStateMenuProps> = ({
  currentApplicationState,
  isApplied,
  isHidden,
  onSetApplicationState,
  onToggleApplied,
  onToggleHide,
  onClose,
  className = '',
  triggerRef,
  usePortal = false,
}) => {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  // Calculate position when using portal
  useEffect(() => {
    if (usePortal && triggerRef?.current && typeof window !== 'undefined') {
      const updatePosition = () => {
        const trigger = triggerRef.current;
        if (!trigger) return;

        const rect = trigger.getBoundingClientRect();
        const scrollY = window.scrollY || window.pageYOffset;
        const scrollX = window.scrollX || window.pageXOffset;
        const menuWidth = menuRef.current?.offsetWidth || 192; // w-48 = 192px

        // Calculate left position - align right edge of menu with right edge of button
        let left = rect.right + scrollX - menuWidth;
        
        // Ensure menu doesn't overflow viewport on the left
        const minLeft = scrollX + 8; // 8px padding from viewport edge
        if (left < minLeft) {
          left = minLeft;
        }

        setPosition({
          top: rect.bottom + scrollY + 8, // mt-2 = 8px
          left,
        });
      };

      // Calculate position immediately
      updatePosition();

      // Also update after menu is rendered to get accurate width
      const timeoutId = setTimeout(() => {
        updatePosition();
      }, 0);

      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    } else if (!usePortal) {
      // Reset position when not using portal
      setPosition(null);
    }
  }, [usePortal, triggerRef]);

  const menuRowClasses = (color: string, isActive: boolean) => {
    const inactive = 'text-jc-ink-secondary hover:bg-jc-elevated-hover';
    if (!isActive) return inactive;

    switch (color) {
      case 'purple':
        return 'bg-[#F5EDD0] dark:bg-[#2A1E08] text-[#6E4F00] dark:text-[#D4A820]';
      case 'green':
        return 'bg-jc-accent-muted dark:bg-[#1A2E22] text-[#3B5E45] dark:text-[#98CFA8]';
      case 'red':
        return 'bg-red-50 dark:bg-red-900/25 text-red-800 dark:text-red-200';
      case 'emerald':
        return 'bg-[#D8EADB] dark:bg-[#1A2E22] text-[#2A4533] dark:text-[#98CFA8]';
      case 'blue':
        return 'bg-jc-accent-muted dark:bg-[#1A2E22] text-[#3B5E45] dark:text-[#98CFA8]';
      default:
        return 'bg-jc-accent-muted dark:bg-[#1A2E22] text-[#3B5E45] dark:text-[#98CFA8]';
    }
  };

  const handleStateClick = (state: ApplicationState) => {
    if (currentApplicationState === state) {
      onSetApplicationState(null);
    } else {
      onSetApplicationState(state);
    }
    onClose();
  };

  const handleAppliedClick = () => {
    onToggleApplied();
    onClose();
  };

  const handleHideClick = () => {
    onToggleHide();
    onClose();
  };

  // Don't render if using portal but position isn't ready yet
  if (usePortal && !position) {
    return null;
  }

  const menuContent = (
    <div 
      ref={menuRef}
      data-application-state-menu="true"
      className={`${usePortal ? 'fixed' : 'absolute right-0 top-full mt-2'} w-48 rounded-xl shadow-lg backdrop-blur-xl bg-jc-surface/98 border border-jc-border z-[9999] overflow-hidden ${className}`}
      style={usePortal && position ? {
        top: `${position.top}px`,
        left: `${position.left}px`,
      } : undefined}
    >
      {/* Mutually exclusive application states */}
      {APPLICATION_STATES.map((stateConfig) => {
        const isActive = currentApplicationState === stateConfig.value;
        const iconName = isActive ? stateConfig.iconSolid : stateConfig.icon;
        const iconVariant = isActive ? 'solid' : 'outline';

        return (
          <button
            key={stateConfig.value || 'none'}
            className={`w-full px-4 py-3 text-left text-sm flex items-center gap-3 transition-colors cursor-pointer ${menuRowClasses(stateConfig.color, isActive)}`}
            onClick={() => handleStateClick(stateConfig.value)}
          >
            <Icon name={iconName} variant={iconVariant} useMaskForColor className={`h-5 w-5 ${isActive ? '' : 'opacity-60'}`} />
            <span className="flex-1">
              {isActive ? (
                <span className="font-medium">
                  {formatMessage({ id: stateConfig.labelKey, defaultMessage: stateConfig.defaultLabel })} ✓
                </span>
              ) : (
                <Msg
                  id="hit.markAsState"
                  defaultMessage="Mark as {state}"
                  values={{ state: formatMessage({ id: stateConfig.labelKey, defaultMessage: stateConfig.defaultLabel }) }}
                  description="Menu item label template for marking job with a specific application state, where {state} is the state name (e.g., Applied, Interviewing)"
                />
              )}
            </span>
          </button>
        );
      })}
      
      {/* Separator */}
      <div className="border-t border-jc-border my-1" />
      
      {/* Independent flags (can coexist with mutually exclusive states) */}
      {/* Applied flag */}
      <button
        className={`w-full px-4 py-3 text-left text-sm flex items-center gap-3 transition-colors cursor-pointer ${
          isApplied
            ? 'bg-jc-accent-muted dark:bg-[#1A2E22] text-[#3B5E45] dark:text-[#98CFA8]'
            : 'text-jc-ink-secondary hover:bg-jc-elevated-hover'
        }`}
        onClick={handleAppliedClick}
      >
        {isApplied ? (
          <Icon name="checkCircle" variant="solid" useMaskForColor className="h-5 w-5" />
        ) : (
          <Icon name="checkCircle" useMaskForColor className="h-5 w-5 opacity-60" />
        )}
        <span className="flex-1">
          {isApplied ? (
            <Msg 
              id="hit.removeApplied" 
              defaultMessage="Remove from applied" 
              description="Menu item label to remove the applied status from a job" 
            />
          ) : (
            <Msg 
              id="hit.markAsApplied.menu" 
              defaultMessage="Mark as Applied" 
              description="Menu item label to mark job as applied" 
            />
          )}
        </span>
      </button>
      
      {/* Hidden flag */}
      <button
        className={`w-full px-4 py-3 text-left text-sm flex items-center gap-3 transition-colors cursor-pointer ${
          isHidden ? 'bg-jc-elevated text-jc-ink-secondary' : 'text-jc-ink-secondary hover:bg-jc-elevated-hover'
        }`}
        onClick={handleHideClick}
      >
        <Icon name="eyeSlash" useMaskForColor className={`h-5 w-5 ${isHidden ? '' : 'opacity-60'}`} />
        <span className="flex-1">
          {isHidden ? (
            <Msg 
              id="hit.unhide" 
              defaultMessage="Unhide job" 
              description="Menu item label to unhide a previously hidden job" 
            />
          ) : (
            <Msg 
              id="hit.hide.menu" 
              defaultMessage="Hide" 
              description="Menu item label to hide a job" 
            />
          )}
        </span>
      </button>
    </div>
  );

  if (usePortal && typeof document !== 'undefined') {
    // Only render portal if position is calculated (or we're still calculating)
    // The visibility: hidden style will hide it until position is set
    return createPortal(menuContent, document.body);
  }

  // When not using portal, return the menu content normally
  return menuContent;
};
